import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckSquare, Link2, Pencil, Plus, Search, Trash2, Unlink, ExternalLink } from 'lucide-react';
import { tasksApi } from '../../api/tasks';
import { ticketsApi } from '../../api/tickets';
import Modal from '../Modal';
import MessageContent from '../MessageContent';
import { StatusBadge } from '../badges';
import { useConfirm } from '../ConfirmDialog';
import TaskAssigneeSelect from './TaskAssigneeSelect';
import TaskActionPicker, { type TaskAction } from './TaskActionPicker';

/* One task, up close: title/details (editable), who it's waiting on, the
   full ticket it's linked to, its notes, and the actions. Works from the
   parent ticket page and from "Tasks for you" — including for an assignee
   who cannot open the parent (the server scopes what comes back). */

interface Props {
  ticketId: string;
  taskId: string;
  linkBase?: string;
  members: any[];
  myId?: string | null;
  emailIncludeContent?: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' at ' + new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? e?.message ?? fallback;

export default function TaskDetailModal({ ticketId, taskId, linkBase = '/tickets', members, myId, emailIncludeContent, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket-task', ticketId, taskId],
    queryFn: () => tasksApi.notes(ticketId, taskId),
  });

  const [note, setNote] = useState('');
  // What the note does to the task, staged next to the one primary button —
  // the same shape as staging a status change on a reply.
  const [action, setAction] = useState<TaskAction>('none');
  const [includeContent, setIncludeContent] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [spawnOpen, setSpawnOpen] = useState(false);
  const [spawnTitle, setSpawnTitle] = useState('');
  const [spawnAssignee, setSpawnAssignee] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkFound, setLinkFound] = useState<Array<{ id: string; ticketNumber: string; title: string; status: string }>>([]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ticket-task', ticketId, taskId] });
    qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
    qc.invalidateQueries({ queryKey: ['ticket-activity', ticketId] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['tickets'] });
    onChanged?.();
  };

  const includeOpt = emailIncludeContent ? includeContent : undefined;
  const addNote = useMutation({
    mutationFn: () => tasksApi.addNote(ticketId, taskId, note.trim(), includeOpt),
    onSuccess: () => { setNote(''); toast.success('Note added'); invalidate(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not add the note')),
  });
  const complete = useMutation({
    mutationFn: () => tasksApi.complete(ticketId, taskId, note.trim(), includeOpt),
    onSuccess: () => { setNote(''); setAction('none'); toast.success('Task completed'); invalidate(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not complete the task')),
  });
  const reopen = useMutation({
    mutationFn: () => tasksApi.reopen(ticketId, taskId, note.trim() || undefined),
    onSuccess: () => { setNote(''); setAction('none'); toast.success('Task reopened'); invalidate(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not reopen the task')),
  });
  const update = useMutation({
    mutationFn: (patch: { title?: string; description?: string | null; assignedToId?: string | null }) =>
      tasksApi.update(ticketId, taskId, patch),
    onSuccess: (t, patch) => {
      setEditingTitle(false); setEditingDesc(false);
      if (patch.assignedToId !== undefined) toast.success(t.assignedTo ? `Assigned to ${t.assignedTo.id === myId ? 'you' : t.assignedTo.name}` : 'Task unassigned');
      else toast.success('Task updated');
      invalidate();
    },
    onError: (e: any) => toast.error(errMsg(e, 'Could not update the task')),
  });
  const spawn = useMutation({
    mutationFn: () => tasksApi.spawnTicket(ticketId, taskId, { title: spawnTitle.trim() || undefined, assignedToId: spawnAssignee || null }),
    onSuccess: ({ ticket }) => { setSpawnOpen(false); toast.success(`Created ${ticket.ticketNumber}`); invalidate(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not create the ticket')),
  });
  const lookup = useMutation({
    mutationFn: (numbers: string) => ticketsApi.lookupByNumbers(numbers),
    onSuccess: (found) => {
      const list = found.filter(t => t.id !== ticketId);
      if (list.length === 0) toast.error('No matching ticket found');
      setLinkFound(list);
    },
    onError: (e: any) => toast.error(errMsg(e, 'Lookup failed')),
  });
  const link = useMutation({
    mutationFn: (linkedTicketId: string | null) => tasksApi.link(ticketId, taskId, linkedTicketId),
    onSuccess: (t) => { setLinkOpen(false); setLinkFound([]); setLinkInput(''); toast.success(t.linkedTicket ? `Linked ${t.linkedTicket.ticketNumber}` : 'Ticket unlinked'); invalidate(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not link the ticket')),
  });
  const remove = useMutation({
    mutationFn: () => tasksApi.remove(ticketId, taskId),
    onSuccess: () => { toast.success('Task deleted'); invalidate(); onClose(); },
    onError: (e: any) => toast.error(errMsg(e, 'Could not delete the task')),
  });

  const busy = addNote.isPending || complete.isPending || reopen.isPending || update.isPending || spawn.isPending || link.isPending || remove.isPending;

  const task = data?.task;
  const finished = task?.status === 'completed';
  const canManage = !!data?.canManage;
  const canAdminister = !!data?.canAdminister;

  return (
    <Modal
      open
      onClose={onClose}
      busy={busy}
      width={720}
      title={
        data ? (
          <span className="flex items-center gap-2 flex-wrap text-sm font-normal">
            <span className="text-muted">Task on</span>
            <span className="font-mono font-semibold">{data.ticket.ticketNumber}</span>
            <span className="text-muted truncate max-w-[20rem]" title={data.ticket.title}>{data.ticket.title}</span>
          </span>
        ) : 'Task'
      }
    >
      {isLoading ? (
        <p className="text-sm text-muted py-4">Loading…</p>
      ) : error || !data || !task ? (
        <p className="text-sm text-err py-4">{errMsg(error, 'This task could not be loaded.')}</p>
      ) : (
        <div className="space-y-4">
          {/* Title */}
          <div className="flex items-start gap-2">
            {editingTitle ? (
              <form
                className="flex-1 flex items-center gap-2"
                onSubmit={e => { e.preventDefault(); if (titleDraft.trim()) update.mutate({ title: titleDraft.trim() }); }}
              >
                <input autoFocus className="input text-base font-semibold flex-1" value={titleDraft} onChange={e => setTitleDraft(e.target.value)} aria-label="Task title" />
                <button type="submit" className="btn btn-primary" disabled={!titleDraft.trim() || update.isPending}>Save</button>
                <button type="button" className="btn btn-ghost" onClick={() => setEditingTitle(false)}>Cancel</button>
              </form>
            ) : (
              <>
                <h3 className={`text-lg font-semibold flex-1 min-w-0 ${finished ? 'line-through text-muted' : 'text-fg'}`}>{task.title}</h3>
                {canManage && (
                  <button type="button" className="btn btn-ghost text-xs py-1 px-2 shrink-0" onClick={() => { setTitleDraft(task.title); setEditingTitle(true); }} title="Rename">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* Status / assignee / linked ticket */}
          <div className="grid gap-2 sm:grid-cols-2 text-sm">
            <div className="rounded-lg bg-row-odd px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted mb-1">Status</p>
              {finished ? (
                <p className="text-fg inline-flex items-center gap-1.5">
                  <CheckSquare className="w-4 h-4" style={{ color: 'var(--scpa-status-ok)' }} />
                  Completed{task.completedBy ? ` by ${task.completedBy.id === myId ? 'you' : task.completedBy.name}` : ''}{task.completedAt ? ` · ${fmt(task.completedAt)}` : ''}
                </p>
              ) : (
                <p className="text-fg">
                  Open{task.assignedTo ? (
                    <span className="font-semibold" style={{ color: task.assignedTo.id === myId ? 'var(--scpa-primary)' : 'var(--scpa-status-warning)' }}>
                      {task.assignedTo.id === myId ? ' · yours' : ` · waiting on ${task.assignedTo.name}`}
                    </span>
                  ) : ' · unassigned'}
                </p>
              )}
            </div>
            <div className="rounded-lg bg-row-odd px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted mb-1">Assignee</p>
              {canManage && !finished ? (
                <TaskAssigneeSelect
                  value={task.assignedToId ?? ''}
                  members={members}
                  myId={myId}
                  disabled={update.isPending}
                  onChange={v => update.mutate({ assignedToId: v || null })}
                  className="select text-sm w-full"
                />
              ) : (
                <p className="text-fg">{task.assignedTo?.name ?? 'Unassigned'}</p>
              )}
            </div>
          </div>

          {/* Linked full ticket */}
          <div className="rounded-lg border border-line-light px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted inline-flex items-center gap-1.5">
                <Link2 className="w-3 h-3" /> Linked ticket
              </p>
              {canAdminister && !task.linkedTicket && (
                <span className="flex items-center gap-1">
                  <button type="button" className="btn btn-ghost text-xs py-1 px-2" onClick={() => { setSpawnTitle(task.title); setSpawnOpen(v => !v); setLinkOpen(false); }}>
                    <Plus className="w-3 h-3" /> Create ticket from task
                  </button>
                  <button type="button" className="btn btn-ghost text-xs py-1 px-2" onClick={() => { setLinkOpen(v => !v); setSpawnOpen(false); }}>
                    <Search className="w-3 h-3" /> Link existing
                  </button>
                </span>
              )}
              {canAdminister && task.linkedTicket && (
                <button type="button" className="btn btn-ghost text-xs py-1 px-2" onClick={() => link.mutate(null)} disabled={link.isPending}>
                  <Unlink className="w-3 h-3" /> Unlink
                </button>
              )}
            </div>
            {task.linkedTicket ? (
              <p className="mt-1.5 flex items-center gap-2 flex-wrap">
                <Link to={`${linkBase}/${task.linkedTicket.id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                  <span className="font-mono font-semibold">{task.linkedTicket.ticketNumber}</span>
                  <span className="truncate max-w-[22rem]">{task.linkedTicket.title}</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
                <StatusBadge status={task.linkedTicket.status} />
                {task.linkedTicket.assignedTo && <span className="text-xs text-muted">assigned to {task.linkedTicket.assignedTo.name}</span>}
                <span className="text-[11px] text-muted basis-full">
                  This task completes when that ticket is resolved or closed, and reopens if it is reopened.
                </span>
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted">
                None. {canAdminister ? 'Turn this task into a full ticket for someone to work, or link one that already exists.' : ''}
              </p>
            )}

            {spawnOpen && (
              <form className="mt-2 pt-2 border-t border-line-light space-y-2" onSubmit={e => { e.preventDefault(); spawn.mutate(); }}>
                <input className="input text-sm" value={spawnTitle} onChange={e => setSpawnTitle(e.target.value)} placeholder="Ticket title" aria-label="New ticket title" />
                <div className="flex items-center gap-2 flex-wrap">
                  <TaskAssigneeSelect value={spawnAssignee} members={members} myId={myId} onChange={setSpawnAssignee} className="select text-sm max-w-[14rem]" />
                  <button type="submit" className="btn btn-primary" disabled={spawn.isPending}>{spawn.isPending ? 'Creating…' : 'Create ticket'}</button>
                  <button type="button" className="btn btn-ghost" onClick={() => setSpawnOpen(false)}>Cancel</button>
                </div>
                <p className="text-[11px] text-muted">
                  The new ticket links back here as its parent, so its assignee sees this history. Its description starts with this task's details.
                </p>
              </form>
            )}

            {linkOpen && (
              <div className="mt-2 pt-2 border-t border-line-light space-y-2">
                <form className="flex items-center gap-2" onSubmit={e => { e.preventDefault(); if (linkInput.trim()) lookup.mutate(linkInput.trim()); }}>
                  <input className="input text-sm flex-1" value={linkInput} onChange={e => setLinkInput(e.target.value)} placeholder="Ticket number, e.g. 213 or cTKT-00213" aria-label="Ticket number to link" />
                  <button type="submit" className="btn btn-secondary" disabled={!linkInput.trim() || lookup.isPending}>
                    <Search className="w-3.5 h-3.5" /> {lookup.isPending ? 'Finding…' : 'Find'}
                  </button>
                </form>
                {linkFound.map(t => (
                  <div key={t.id} className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs">{t.ticketNumber}</span>
                    <span className="truncate flex-1">{t.title}</span>
                    <StatusBadge status={t.status} />
                    <button type="button" className="btn btn-primary text-xs py-1 px-2" onClick={() => link.mutate(t.id)} disabled={link.isPending}>Link</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">Details</p>
              {canManage && !editingDesc && (
                <button type="button" className="btn btn-ghost text-xs py-1 px-2" onClick={() => { setDescDraft(task.description ?? ''); setEditingDesc(true); }}>
                  <Pencil className="w-3 h-3" /> {task.description ? 'Edit' : 'Add details'}
                </button>
              )}
            </div>
            {editingDesc ? (
              <form className="space-y-2" onSubmit={e => { e.preventDefault(); update.mutate({ description: descDraft.trim() || null }); }}>
                <textarea autoFocus className="input resize-y text-sm" rows={3} value={descDraft} onChange={e => setDescDraft(e.target.value)} aria-label="Task details" />
                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={update.isPending}>Save</button>
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingDesc(false)}>Cancel</button>
                </div>
              </form>
            ) : task.description ? (
              <MessageContent content={task.description} />
            ) : (
              <p className="text-xs text-muted">No details.</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted mb-1">
              Notes {data.notes.length > 0 && <span className="font-normal normal-case tracking-normal">({data.notes.length})</span>}
            </p>
            {data.notes.length === 0 ? (
              <p className="text-xs text-muted">No notes yet.</p>
            ) : (
              <ul className="space-y-2">
                {data.notes.map((n: any) => (
                  <li key={n.id} className="rounded-lg px-3 py-2" style={{ background: 'var(--scpa-primary-muted)' }}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-semibold text-fg">{n.authorName ?? n.author?.name ?? 'Unknown'}</span>
                      <span className="text-[11px] text-muted">{fmt(n.createdAt)}</span>
                    </div>
                    <MessageContent content={String(n.content ?? '')} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Compose: one primary action (add the note). Completing or
              reopening is *staged* beside it — never a second button of
              equal weight, which is too easy to hit by accident. */}
          <div className="space-y-2">
            <textarea
              className="input resize-y text-sm"
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={finished ? 'Add a note about this completed task…' : 'Add a progress note…'}
              aria-label="Task note"
            />
            {emailIncludeContent && (
              <label className="flex items-start gap-2 cursor-pointer text-xs">
                <input type="checkbox" className="w-3.5 h-3.5 mt-0.5 accent-[var(--scpa-primary)]" checked={includeContent} onChange={e => setIncludeContent(e.target.checked)} />
                <span>Include this note's text in notification emails</span>
              </label>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || (action === 'reopen' ? false : !note.trim())}
                title={action === 'complete' && !note.trim() ? 'Write a note first — completing a task requires one' : undefined}
                onClick={() => {
                  if (action === 'complete') complete.mutate();
                  else if (action === 'reopen') reopen.mutate();
                  else addNote.mutate();
                }}
              >
                {action === 'complete' && <CheckSquare className="w-3.5 h-3.5" />}
                {busy && (addNote.isPending || complete.isPending || reopen.isPending)
                  ? 'Saving…'
                  : action === 'complete' ? 'Add note & complete'
                  : action === 'reopen' ? 'Add note & reopen'
                  : 'Add note'}
              </button>

              {canManage && (
                <TaskActionPicker
                  status={task.status}
                  staged={action}
                  onSelect={setAction}
                  onClear={() => setAction('none')}
                  disabled={busy}
                />
              )}

              {canAdminister && (
                <button
                  type="button"
                  className="btn btn-ghost text-err ml-auto"
                  disabled={busy}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Delete this task?',
                      message: `"${task.title}" is removed from the checklist. Its notes stay in the conversation.`,
                      confirmLabel: 'Delete task',
                      danger: true,
                    });
                    if (ok) remove.mutate();
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
            {action === 'complete' && (
              <p className="text-xs text-muted">
                This note is required — it is saved as the record of how the task was finished, and the task is
                marked complete when you add it.
              </p>
            )}
            {action === 'reopen' && (
              <p className="text-xs text-muted">The task goes back to open when you add this. A note is optional here.</p>
            )}
            {!canManage && (
              <p className="text-[11px] text-muted">Only staff, collaborators, and the assignee can complete or reassign this task.</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
